/**
 * ProfileForm Component
 * คอมโพเนนต์ฟอร์มโปรไฟล์
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { authApi } from '../auth.api'
import { useAuthStore } from '../auth.store'
import { Loader2, CheckCircle } from 'lucide-react'
import type { User } from '../types'

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
})

type ProfileFormValues = z.infer<typeof profileSchema>

interface ProfileFormProps {
  user: User
}

export function ProfileForm({ user }: ProfileFormProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
  })

  const onSubmit = async (values: ProfileFormValues) => {
    setIsLoading(true)
    setSuccess(false)
    try {
      const updatedUser = await authApi.updateProfile(values)
      useAuthStore.getState().setUser(updatedUser)
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch {
      // Error handling
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e)
      }}
      className="max-w-md space-y-5"
    >
      {success && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          {t('auth.profileUpdated')}
        </div>
      )}

      {/* Email (read-only) */}
      <div className="space-y-2">
        <label className="text-muted-foreground text-sm font-medium">{t('auth.email')}</label>
        <input
          type="email"
          value={user.email}
          disabled
          className="border-input bg-muted text-muted-foreground h-10 w-full rounded-lg border px-3 text-sm"
        />
      </div>

      {/* First Name */}
      <div className="space-y-2">
        <label htmlFor="profile-firstName" className="text-sm font-medium">
          {t('auth.firstName')}
        </label>
        <input
          id="profile-firstName"
          className={`bg-background focus:ring-primary/30 h-10 w-full rounded-lg border px-3 text-sm focus:ring-2 focus:outline-none ${
            errors.firstName ? 'border-red-500' : 'border-input'
          }`}
          {...register('firstName')}
        />
        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName.message}</p>}
      </div>

      {/* Last Name */}
      <div className="space-y-2">
        <label htmlFor="profile-lastName" className="text-sm font-medium">
          {t('auth.lastName')}
        </label>
        <input
          id="profile-lastName"
          className={`bg-background focus:ring-primary/30 h-10 w-full rounded-lg border px-3 text-sm focus:ring-2 focus:outline-none ${
            errors.lastName ? 'border-red-500' : 'border-input'
          }`}
          {...register('lastName')}
        />
        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName.message}</p>}
      </div>

      {/* Role & Domain (read-only) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-muted-foreground text-sm font-medium">{t('auth.role')}</label>
          <div className="border-input bg-muted text-muted-foreground h-10 rounded-lg border px-3 py-2 text-sm">
            {user.role}
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-muted-foreground text-sm font-medium">{t('auth.lastLogin')}</label>
          <div className="border-input bg-muted text-muted-foreground h-10 rounded-lg border px-3 py-2 text-sm">
            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : '—'}
          </div>
        </div>
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('actions.save')}
      </Button>
    </form>
  )
}
